import { CryptoAsset } from '../../types/crypto';

// Using Blockscout public API (no API key required) to fetch Native and ERC20 tokens
export const fetchEvmData = async (address: string, chain: 'ethereum' | 'polygon') => {
  const baseUrl = chain === 'ethereum' ? 'https://eth.blockscout.com/api/v2' : 'https://polygon.blockscout.com/api/v2';
  
  try {
    // Fetch native balance
    const addrRes = await fetch(`${baseUrl}/addresses/${address}`);
    if (!addrRes.ok) throw new Error(`Failed to fetch ${chain} address data`);
    const addrData = await addrRes.json();
    if (addrData.coin_balance === undefined) {
      throw new Error(`Rate limit or invalid payload from ${chain} blockscout`);
    }
    
    const nativeBalanceStr = addrData.coin_balance;
    const nativeBalance = parseInt(nativeBalanceStr) / 1e18;
    const nativeUsdValue = addrData.exchange_rate ? nativeBalance * parseFloat(addrData.exchange_rate) : 0;
    const nativePriceUsd = parseFloat(addrData.exchange_rate || "0");

    // Fetch ERC-20 tokens
    const tokensRes = await fetch(`${baseUrl}/addresses/${address}/token-balances`);
    let assets: CryptoAsset[] = [];
    
    if (tokensRes.ok) {
      const tokensData = await tokensRes.json();
      const tokens = Array.isArray(tokensData) ? tokensData : (tokensData.items || []);
      
      assets = tokens
        .filter((t: any) => t.token && t.token.type === 'ERC-20' && t.value && t.value !== "0")
        .map((t: any) => {
          const decimals = parseInt(t.token.decimals || "18");
          const balance = parseFloat(t.value) / Math.pow(10, decimals);
          
          let priceUsd = 0;
          let usdValue = 0;
          if (t.token.exchange_rate) {
            priceUsd = parseFloat(t.token.exchange_rate);
            usdValue = balance * priceUsd;
          }

          return {
            id: t.token.address,
            name: t.token.name || t.token.symbol || 'Unknown Token',
            symbol: t.token.symbol || 'UNK',
            balance,
            usdValue,
            priceUsd,
            icon: t.token.icon_url,
            contractAddress: t.token.address
          };
        })
        
      const fetchUsdcBalanceRpc = async (addr: string, ch: 'ethereum' | 'polygon'): Promise<number | null> => {
        try {
          const rpcUrl = ch === 'ethereum' ? 'https://eth.drpc.org' : 'https://polygon.drpc.org';
          const usdcContract = ch === 'ethereum' ? '0xa0b86991c6218b36c1d19d4a2e9eb0ce3606eb48' : '0x3c499c542cEF5E3811e1192ce70d8cC03d5c3359';
          const paddedAddress = addr.toLowerCase().replace('0x', '').padStart(64, '0');
          const data = `0x70a08231${paddedAddress}`;
          const res = await fetch(rpcUrl, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ jsonrpc: '2.0', method: 'eth_call', params: [{ to: usdcContract, data }, 'latest'], id: 1 })
          });
          if (res.ok) {
            const json = await res.json();
            if (json.result && json.result !== '0x') {
              return parseInt(json.result, 16) / 1e6;
            }
          }
        } catch (e) {
          console.error('RPC fetch failed', e);
        }
        return null;
      };

      const liveUsdcBalance = await fetchUsdcBalanceRpc(address, chain);
      if (liveUsdcBalance !== null) {
        let usdcAsset = assets.find((a: CryptoAsset) => a.symbol === 'USDC');
        if (usdcAsset) {
          usdcAsset.balance = liveUsdcBalance;
          usdcAsset.usdValue = liveUsdcBalance * (usdcAsset.priceUsd || 1);
        } else {
          assets.push({
            id: chain === 'ethereum' ? '0xa0b86991c6218b36c1d19d4a2e9eb0ce3606eb48' : '0x3c499c542cEF5E3811e1192ce70d8cC03d5c3359',
            name: 'USDC',
            symbol: 'USDC',
            balance: liveUsdcBalance,
            usdValue: liveUsdcBalance,
            priceUsd: 1,
            icon: 'https://assets.coingecko.com/coins/images/6319/small/USDC.png',
            contractAddress: chain === 'ethereum' ? '0xa0b86991c6218b36c1d19d4a2e9eb0ce3606eb48' : '0x3c499c542cEF5E3811e1192ce70d8cC03d5c3359'
          });
        }
      }

      assets.sort((a: CryptoAsset, b: CryptoAsset) => b.usdValue - a.usdValue);
    }

    return {
      nativeBalance,
      nativeUsdValue,
      nativePriceUsd,
      assets
    };
  } catch (error) {
    console.error(`Error fetching ${chain} data:`, error);
    throw error;
  }
};
